import { Component } from '@theme/component';

/**
 * A custom element that manages a card that can open/close additional content.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement[]} [media] - The image that's tab controlled
 * @property {HTMLDivElement[]} [content] - The content that's tab controlled. Either the images or the content should be there, but not both.
 * @property {HTMLButtonElement[]} tab - The button that handles the clicks
 *
 *
 * @extends {Component<Refs>}
 */
class TabbedMedia extends Component {
  constructor() {
    super();

    this.currentTab = null;
  }
  connectedCallback() {
    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const name = el.getAttribute('ref');
      if (this.refs[name]) {
        // If already exists, convert to array
        if (!Array.isArray(this.refs[name])) {
          this.refs[name] = [this.refs[name]];
        }
        this.refs[name].push(el);
      } else {
        this.refs[name] = el;
      }
    });
    this.tabs = [...this.querySelectorAll('[ref="tab"]')];
    if (this.refs.media) {
      this.swiper = undefined;
      const matchMedia = window.matchMedia('(max-width: 1280px)');
      window.addEventListener('DOMContentLoaded', () => {
        this.handleSwiper(matchMedia);
        matchMedia.addEventListener('change', () =>
          this.handleSwiper(matchMedia),
        );
        if (this.getAttribute('data-cycle') == 'true') {
          this.currentTab = this.querySelector('[ref="tab"].active');
          if (this.currentTab) {
            setInterval(() => {
              let nextTab = this.tabs[this.tabs.indexOf(this.currentTab) + 1];
              if (!nextTab || !nextTab.hasAttribute('ref')) {
                nextTab = this.tabs[0];
              }
              this.handleOpen({ target: nextTab });
              this.currentTab = nextTab;
            }, 8000);
          }
        }
      });
    }

    window.addEventListener('hashchange', () => {
      const hash = window.location.hash.substring(1);
      const tab = this.querySelector(`[id="${hash}"]`);
      if (tab) {
        tab.click();
      }
    });
  }

  /**
   * Handles opening and closing of nearest neighbour
   *
   */
  handleOpen({ event, target }) {
    const tabs = this.querySelectorAll('[ref="tab"]');
    const content = this.querySelectorAll('[ref^="content"]');
    const media = this.querySelectorAll('[ref^="media"]');
    let thingsToTab = [];

    if (content.length > 0) {
      thingsToTab = content;
    } else if (media) {
      thingsToTab = media;
    }

    const clickTarget = event ? event.target : target;

    tabs.forEach((tab, index) => {
      if (tab.classList.contains('active')) {
        if (tab == clickTarget) {
          return;
        }
        tab.classList.remove('active');
        if (thingsToTab[index]) {
          thingsToTab[index].classList.remove('active');
          thingsToTab[index].classList.remove('tw-fade-in');
          thingsToTab[index].classList.add('tw-fade-out');
          if (thingsToTab == content) {
            thingsToTab[index].classList.remove('h-[auto]');
            thingsToTab[index].classList.add('h-[0px]');
          }
        }
      } else if (tab == clickTarget) {
        tab.classList.add('active');
        this.currentTab = tab;
        if (thingsToTab[index]) {
          thingsToTab[index].classList.add('active');
          thingsToTab[index].classList.remove('tw-fade-out');
          thingsToTab[index].classList.add('tw-fade-in');
          thingsToTab[index].classList.remove('h-[0px]');
          thingsToTab[index].classList.add('h-[auto]');
        }

        if (this.swiper) {
          this.swiper.slideTo(index);
        }
      }
    });
  }

  handleSwiper(matchMedia) {
    if (matchMedia.matches) {
      const swiperContainers = document.querySelectorAll(
        '[id^="tabbed-media-"]',
      );
      swiperContainers.forEach((container) => {
        const swiperElement = container.querySelector('.tw-carousel-tabs');
        if (!swiperElement) return;

        const swiper = new Swiper(swiperElement, {
          slidesPerView: 1.5,
          spaceBetween: 24,
          navigation: {
            nextEl: container.querySelector(
              '.swiper-buttons .swiper-button-next',
            ),
            prevEl: container.querySelector(
              '.swiper-buttons .swiper-button-prev',
            ),
          },
          pagination: {
            el: container.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'progressbar',
          },
          on: {
            slideChange: (swiper) => {
              this.currentTab = this.tabs[swiper.activeIndex];
              this.handleOpen({ target: this.tabs[swiper.activeIndex] });
            },
          },
        });
        swiper.init();
        this.swiper = swiper;
      });
    } else {
      if (this.swiper) {
        this.swiper.destroy();
      }
    }
  }
}

if (!customElements.get('tabbed-media')) {
  customElements.define('tabbed-media', TabbedMedia);
}
